import { notification } from 'antd';
import axios, { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { formatDate } from '/react4xp/mixins/helpers';
import {
    PaymentData,
    TData,
} from '/react4xp/parts/find-taxpayer-service/find-taxpayer-service.type';

const urlSync = `/services/isnaportalsync/open-api/taxpayer-data`;

export const urlPrint = '/services/isnaportalsync/open-api/taxpayer-data';

export const getTaxPayerInfo = (
    request: any,
    secretKey?: any,
    token?: any
): Promise<AxiosResponse> => {
    let query = '?';
    if (secretKey) query += `token=${secretKey}`;
    return axios
        .get(urlSync, {
            params: {
                taxpayerCode: request.iinBin,
                firstName: request.firstName,
                lastName: request.lastName,
                name: request.organizationName,
                taxpayerType: request.type,
            },
        })
        .then((response) => {
            if (response.data.errorResponse?.errorMessage) {
                notification['warning']({
                    message: response.data.errorResponse?.errorMessage,
                });
            }

            return response;
        })
        .then((response) => {
            if (
                response.data.taxpayerPortalSearchResponses &&
                response.data.taxpayerPortalSearchResponses.length > 0
            ) {
                const rawDataList = response.data.taxpayerPortalSearchResponses || [];

                if (request.type === 'UL') {
                    return {
                        ...response,
                        data: {
                            taxpayers: getIpUlTaxPayerInfo(rawDataList, request.type),
                            answerDto: response.data?.answerDto ?? null,
                            total: 1,
                        },
                    };
                }

                if (request.type === 'IP') {
                    return {
                        ...response,
                        data: { taxpayers: getIpUlTaxPayerInfo(rawDataList, request.type) },
                        total: 1,
                    };
                }

                const groupedMap = new Map<string, TData>();

                rawDataList.forEach((taxpayer) => {
                    let iinBin = taxpayer.code;
                    if (['IP', 'UL'].includes(request.type) && !request.iinBin) {
                        iinBin = '';
                    }

                    let rows = [];
                    if (taxpayer.taxpayerType === 'LZCHP' && Array.isArray(taxpayer.lzchpTypes)) {
                        rows = taxpayer.lzchpTypes.map((typeItem) => ({
                            beginDate: typeItem.beginDate,
                            endDate: typeItem.endDate,
                            lzchpType: typeItem.lzchpType,
                            reason: taxpayer.additionalInfo?.ru || '',
                            rnn: iinBin,
                            suspensionPeriod: getSuspensionPeriod(taxpayer),
                            ulTaxInfo: [],
                            endReason: typeItem.endReason,
                            additionalInfoDocument: taxpayer.additionalInfoDocument
                                ? {
                                      ...taxpayer.additionalInfoDocument,
                                      date: formatDate(taxpayer.additionalInfoDocument.date),
                                  }
                                : null,
                        }));
                    } else {
                        rows = [
                            {
                                beginDate: taxpayer.beginDate,
                                endDate: taxpayer.endDate,
                                lzchpType: taxpayer.taxpayerType,
                                reason: taxpayer.additionalInfo?.ru || '',
                                rnn: iinBin,
                                suspensionPeriod: getSuspensionPeriod(taxpayer),
                                ulTaxInfo: [],
                                endReason: taxpayer.endReason,
                                additionalInfoDocument: taxpayer.additionalInfoDocument
                                    ? {
                                          ...taxpayer.additionalInfoDocument,
                                          date: formatDate(taxpayer.additionalInfoDocument.date),
                                      }
                                    : null,
                            },
                        ];
                    }

                    if (groupedMap.has(iinBin)) {
                        groupedMap.get(iinBin)!.rows.push(...rows);
                    } else {
                        groupedMap.set(iinBin, {
                            iinBin,
                            name: getTaxpayerName(taxpayer),
                            type: taxpayer.taxpayerType,
                            rows,
                        });
                    }
                });

                const groupedList: TData[] = Array.from(groupedMap.values());

                return {
                    ...response,
                    data: { taxpayers: groupedList },
                    total: 1,
                };
            }

            return response;
        });
};

const getSuspensionPeriod = (taxpayer: any): string | undefined => {
    const begin = taxpayer?.additionalInfo?.beginDate;
    const end = taxpayer?.additionalInfo?.endDate;

    if (begin && end) return `${formatDate(begin)} - ${formatDate(end)}`;
    if (begin) return `${formatDate(begin)}`;
    if (end) return `${formatDate(end)}`;
    return '';
};

const getTaxpayerName = (taxpayer: any): string | undefined => {
    if (taxpayer?.name) {
        return taxpayer.name;
    }

    if (taxpayer?.taxpayerName) {
        return taxpayer.taxpayerName;
    }

    const { lastName, firstName, middleName } = taxpayer?.fullName || {};


    return [lastName, firstName, middleName].join(' ');
};

const getIpUlTaxPayerInfo = (data: any, type: string): TData[] => {
    const rows = [];
    data.forEach((taxpayer, index) => {
        rows.push({
            rowNumber: index + 1,
            name: taxpayer.name,
            type: taxpayer.taxpayerType,
            iinBin: taxpayer.code,
            beginDate: taxpayer.beginDate,
            endDate: taxpayer.endDate,
            endReason: taxpayer.endReason,
            additionalInfoDocument: taxpayer.additionalInfoDocument
                ? {
                      ...taxpayer.additionalInfoDocument,
                      date: formatDate(taxpayer.additionalInfoDocument.date),
                  }
                : null,
            suspensionPeriod: getSuspensionPeriod(taxpayer),
        });
    });

    return [
        {
            iinBin: data[0]?.code || '',
            name: getTaxpayerName(data[0]),
            type,
            rows,
        },
    ];
};

type PaymentAnswer = {
    payment?: Array<{
        summa: number;
        receiptDate: string;
        payType: 1 | 2 | 3;
        payStatus: string;
        kbk: string;
    }>;
};

const getPaymentInfo = (data: PaymentAnswer): PaymentData => {
    const thisYear = dayjs().year();
    const isValidYear = (year: number) => {
        return year >= thisYear - 5 && year <= thisYear;
    };
    const getColumns = () => {
        const res = {};
        for (let y = thisYear; y >= thisYear - 5; y--) {
            res[y] = 0;
        }
        return res;
    };

    const taxRevenue = {
        fine: getColumns(),
        penalty: getColumns(),
        tax: getColumns(),
    };
    const ndsRevenue = {
        tax: getColumns(),
    };

    (data.payment || []).map((payment) => {
        if (!payment.receiptDate.trim()) {
            return;
        }
        const year = dayjs(payment.receiptDate).year();
        if (!isValidYear(year)) {
            return;
        }

        if (
            ![2, 3, 4, 8, 12, 13, 14, 15, 17, 20, 24, 25].includes(
                parseInt(payment.payStatus.match(/^\d+/)[0], 10)
            )
        ) {
            return;
        }

        if (payment.kbk === '105101' && payment.payType === 1) {
            ndsRevenue.tax[year] += payment.summa;
            if (year === 2023) {
                console.log(payment.summa);
            }
        }

        if (payment.payType === 1) {
            taxRevenue.tax[year] += payment.summa;
            return;
        }
        if (payment.payType === 2) {
            taxRevenue.penalty[year] += payment.summa;
            return;
        }
        if (payment.payType === 3) {
            taxRevenue.fine[year] += payment.summa;
            return;
        }
    });

    return {
        taxRevenue,
        ndsRevenue,
    };
};
